import { useState } from "react";
import useSWR from "swr";

import { getSWRKey, SwrStore, useInterval } from "~utils";

import { useLastPromptPersistence } from "./use-last-prompt-persistence";

export function usePromptLimit(): usePromptLimitReturn {
  const { lastPrompted, setLastPrompted } = useLastPrompted();
  const isRecentlyPrompted = useIsRecentlyPrompted(lastPrompted);

  return { canPrompt: !isRecentlyPrompted, reportPrompted: setLastPrompted };
}

export type usePromptLimitReturn = {
  canPrompt: boolean;
  reportPrompted: (date: Date) => void;
};

function useLastPrompted(): useLastPromptedReturn {
  const {
    getLastPrompted: getLastPromptedPersisted,
    setLastPrompted: setLastPromptedPersisted,
  } = useLastPromptPersistence();

  const { data, mutate } = useSWR(
    getSWRKey(SwrStore.LastPrompted),
    getLastPromptedPersisted,
    { suspense: true },
  );

  const lastPrompted = data ?? new Date(0);

  const setLastPrompted = (newLastPrompted: Date): void => {
    if (newLastPrompted < lastPrompted) return;

    mutate(
      setLastPromptedPersisted(newLastPrompted).then(() => newLastPrompted),
      { optimisticData: newLastPrompted, revalidate: false },
    );
  };

  return {
    lastPrompted,
    setLastPrompted,
  };
}

type useLastPromptedReturn = {
  lastPrompted: Date;
  setLastPrompted: (date: Date) => void;
};

// TODO: Split into effect/zustand
function useIsRecentlyPrompted(lastPrompted: Date): boolean {
  const check = (): boolean => {
    const targetDate = new Date(lastPrompted.getTime() + dayMs);
    return new Date() < targetDate;
  };

  const [isRecentlyPrompted, setIsRecentlyPrompted] =
    useState<boolean>(check());

  useInterval((): void => setIsRecentlyPrompted(check()));

  return isRecentlyPrompted;
}

const dayMs = 1000 * 60 * 60 * 24;
